import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../session/api.js';
import { useSession } from '../session/SessionProvider.js';

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
  /** Replace the data locally after a mutation, then reconcile with a reload. */
  set: (next: T) => void;
}

/** Loads one API resource for a screen and exposes a reload. */
export function useLoad<T>(path: string | null): Loaded<T> {
  const { api } = useSession();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));

  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      setData(await api<T>(path));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }, [api, path]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, error, loading, reload, set: setData };
}

export function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Something went wrong. Try again.';
}
